import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Arrow, General } from 'icons/untitledui';
import { InstantReflexVersionRef } from 'shiftNotes/_types';
import { DropdownMenu } from 'shared/components/dropdown/DropdownMenu';

import * as Styled from 'shiftNotes/styles/NotesStack.style';

interface VersionSelectorProps {
  versions: InstantReflexVersionRef[];
  selectedVersionId: string;
  onVersionSelect: (versionRef: InstantReflexVersionRef) => void;
}

export default function VersionSelector({
  versions,
  selectedVersionId,
  onVersionSelect,
}: VersionSelectorProps): JSX.Element | null {
  const { t } = useTranslation('pageShiftNotes');
  const [isOpen, setIsOpen] = useState(false);

  if (versions.length <= 1) return null;

  const selectedVersion = versions.find(
    (version) => version.id === selectedVersionId
  );

  const items = versions.map((version) => ({
    label: t('reflexFeedback.version', { number: version.version }),
    selected: version.id === selectedVersionId,
    icon:
      version.id === selectedVersionId ? <General.Check /> : <General.Circle />,
    onSelect: ({ close }: { close: () => void }) => {
      onVersionSelect(version);
      close();
    },
  }));

  return (
    <Styled.VersionMenuWrapper>
      <DropdownMenu
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        items={items}
        usePortal
        menuWidth="180px"
        renderTrigger={({ toggle, triggerRef }) => (
          <Styled.VersionTrigger
            ref={triggerRef}
            type="button"
            onClick={toggle}
            aria-haspopup="menu"
            aria-expanded={isOpen}
            aria-label={t('reflexFeedback.versionSelector')}
          >
            <Styled.VersionTriggerLabel as="span">
              {t('reflexFeedback.version', {
                number: selectedVersion?.version ?? 1,
              })}
            </Styled.VersionTriggerLabel>
            <Arrow.ChevronDown />
          </Styled.VersionTrigger>
        )}
      />
    </Styled.VersionMenuWrapper>
  );
}
